import React, { useEffect, useRef, useState, useCallback } from "react";
import { Box, Button, Typography, TextField, Tooltip, IconButton } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "system/store";
import { policyEditorActions } from "system/store/slices/policy-editor-slice";
import { IniInfo } from "system/types/interfaces";
import { usePolicyEditorPermissions } from "system/hooks";
import { SitesAndSubnetsTable } from "./SitesAndSubnetsTable";
import { AddSiteSubnetModal } from "./AddSiteSubnetModal";
import { Icon } from "@fluentui/react/lib/Icon";
import { useDebouncedCallback } from "use-debounce";

export const SitesAndSubnetsTab: React.FC = () => {
  const dispatch = useDispatch();
  const sitesAndSubnets = useSelector((state: RootState) => state.policyEditor.sitesAndSubnets || []);
  const { isReadOnly: readOnlyViewer } = usePolicyEditorPermissions();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [insertIndex, setInsertIndex] = useState<number | null>(null);
  const [draftItem, setDraftItem] = useState<IniInfo | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<number[]>([]);
  const [currentResultIndex, setCurrentResultIndex] = useState(-1);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const handleOpenEditModal = (item: IniInfo) => {
    setDraftItem(item);
    setIsAddModalOpen(true);
  };

  const handleAddItem = (newItem: IniInfo) => {
    dispatch(policyEditorActions.addSiteSubnet({ item: newItem, index: insertIndex ?? sitesAndSubnets.length }));
    setInsertIndex(null);
    setIsAddModalOpen(false);
    setDraftItem(null);
  };

  const handleUpdateItem = (updatedItem: IniInfo) => {
    dispatch(policyEditorActions.updateSiteSubnet({ updatedItem }));
    setIsAddModalOpen(false);
    setDraftItem(null);
  };

  const handleDelete = (name: string) => {
    dispatch(policyEditorActions.deleteSiteSubnet(name));
  };

  const handleUndoDelete = (name: string) => {
    dispatch(policyEditorActions.undoDeleteSiteSubnet(name));
  };

  const handleSearch = useDebouncedCallback((term: string) => {
    if (!term) {
      setSearchResults([]);
      setCurrentResultIndex(-1);
      return;
    }

    const results = sitesAndSubnets
      .map((item, idx) => {
        const searchInName = item.name.toLowerCase().includes(term.toLowerCase());
        const searchInValue = item.value.toLowerCase().includes(term.toLowerCase());
        return searchInName || searchInValue ? idx : -1;
      })
      .filter((idx) => idx !== -1);
    setSearchResults(results);
    setCurrentResultIndex(results.length > 0 ? 0 : -1);
  }, 300);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (searchResults.length === 0) return;

      switch (e.key) {
        case "Enter":
        case "ArrowDown":
          e.preventDefault();
          const nextIndex = (currentResultIndex + 1) % searchResults.length;
          setCurrentResultIndex(nextIndex);
          break;
        case "ArrowUp":
          e.preventDefault();
          const prevIndex = (currentResultIndex - 1 + searchResults.length) % searchResults.length;
          setCurrentResultIndex(prevIndex);
          break;
      }
    },
    [currentResultIndex, searchResults]
  );

  useEffect(() => {
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.querySelector("input")?.focus();
      }
    }, 1000);
  }, []);

  return (
    <Box>
      <Typography variant="h6" mb={2} sx={{ color: "#fff" }}>
        Configured Sites and Subnets
      </Typography>
      <Box display="flex" flexDirection={"column"} gap={2} justifyContent="center" alignItems="start" mb={2}>
        <Box display="flex" gap={1} alignItems={"center"}>
          <Tooltip title="Press Enter/Arrow keys to navigate through the results" arrow>
            <TextField
              ref={searchInputRef}
              placeholder="Search sites or IP ranges..."
              variant="outlined"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                handleSearch(e.target.value);
              }}
              onKeyDown={handleKeyDown}
              sx={{ width: "350px" }}
              InputProps={{
                endAdornment: searchResults.length > 0 && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ padding: "0 8px", color: "#fff", opacity: 0.7, borderRight: "1px solid #cccccc" }}
                    >
                      {currentResultIndex + 1}/{searchResults.length}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => {
                        const prevIndex = (currentResultIndex - 1 + searchResults.length) % searchResults.length;
                        setCurrentResultIndex(prevIndex);
                      }}
                      sx={{ color: "#fff", opacity: 0.7, "&:hover": { color: "#4caf50", opacity: 1 } }}
                    >
                      <Icon iconName="ChevronUp" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        const nextIndex = (currentResultIndex + 1) % searchResults.length;
                        setCurrentResultIndex(nextIndex);
                      }}
                      sx={{ color: "#fff", opacity: 0.7, "&:hover": { color: "#4caf50", opacity: 1 } }}
                    >
                      <Icon iconName="ChevronDown" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchTerm("");
                        handleSearch("");
                      }}
                      sx={{ fontSize: "18px", color: "#fff", opacity: 0.7, "&:hover": { color: "#4caf50", opacity: 1 } }}
                    >
                      <Icon iconName="Cancel" style={{ fontSize: "inherit" }} />
                    </IconButton>
                  </Box>
                ),
              }}
            />
          </Tooltip>
        </Box>
        <Button variant="contained" onClick={() => setIsAddModalOpen(true)} hidden={readOnlyViewer}>
          + Add Site
        </Button>
      </Box>
      <SitesAndSubnetsTable
        sitesAndSubnets={sitesAndSubnets}
        onAdd={(index: number) => {
          setInsertIndex(index);
          setIsAddModalOpen(true);
        }}
        onDelete={handleDelete}
        onUndoDelete={handleUndoDelete}
        onEdit={handleOpenEditModal}
        readOnlyViewer={readOnlyViewer}
        searchTerm={searchTerm}
        searchResults={searchResults}
        currentResultIndex={currentResultIndex}
      />
      <AddSiteSubnetModal
        open={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setInsertIndex(null);
          setDraftItem(null);
        }}
        onAdd={handleAddItem}
        onUpdate={handleUpdateItem}
        initialItem={draftItem}
      />
    </Box>
  );
};
